/**
 * Campaign Battlefield
 * Campaign-level view: funnel, lead list, intelligence.
 */

import { Link, useNavigate, useParams } from 'react-router-dom';
import { CampaignConfigDialog } from '@/components/campaign/campaign-config-dialog';
import { Layout } from '@/components/layout/Layout';
import { useAuth } from '@/contexts/AuthContext';
import {
  useArchiveCampaign,
  useIsArchiveCampaignPending,
} from '@/hooks/use-archive-campaign';
import { useIsUpdateCampaignConfigPending } from '@/hooks/use-update-campaign-config';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import { useCampaignDetail, useCampaignLeads, type CampaignLead } from '@/hooks/useCampaigns';
import { useCampaignFunnel, type CampaignFunnelRow } from '@/hooks/useCampaignFunnel';
import { isArchivedCampaign } from '@/hooks/useMissionControl';
import {
  parseFunnelStage,
  parseMomentum,
  parseBANT,
  FUNNEL_STAGE_LABELS,
  FUNNEL_STAGE_COLORS,
  BANT_COLORS,
  MOMENTUM_COLORS,
  getRecencyColor,
  type FunnelStage,
} from '@/lib/intelligence-helpers';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Input } from '@/components/ui/input';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  ArrowLeft,
  Archive,
  ArchiveRestore,
  Target,
  Users,
  Send,
  MessageSquare,
  Clock,
  TrendingUp,
  TrendingDown,
  Minus,
  Search,
  Inbox,
  Info,
  Pencil,
  UserPlus,
  UserCheck,
  Ban,
  Wallet,
  AlertCircle,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useState, useMemo } from 'react';
import {
  formatUiNumber,
  formatUiPercent,
  formatUiRelativeTime,
} from '@/lib/ui-format';
import { toSafeUiError } from '@/lib/user-safe-error';

// ─────────────────────────────────────────────────────────────────────────
// Funnel Bar
// ─────────────────────────────────────────────────────────────────────────

const FUNNEL_DISPLAY_ORDER: FunnelStage[] = [
  'unknown', 'qualifying', 'needs_followup', 'next_step_defined', 'meeting_booked', 'graceful_exit', 'blocked',
];

function CampaignFunnel({
  leads,
  selectedStage,
  onStageClick,
}: {
  leads: CampaignLead[];
  selectedStage: FunnelStage | null;
  onStageClick: (stage: FunnelStage | null) => void;
}): JSX.Element {
  const stageCounts = useMemo(() => {
    const counts = new Map<FunnelStage, number>();
    leads.forEach((lead) => {
      const stage = parseFunnelStage(lead.decision_trace);
      counts.set(stage, (counts.get(stage) || 0) + 1);
    });
    return counts;
  }, [leads]);

  const activeStages = FUNNEL_DISPLAY_ORDER.filter((s) => (stageCounts.get(s) || 0) > 0);

  return (
    <div className="flex flex-wrap gap-2">
      <button
        type="button"
        onClick={() => onStageClick(null)}
        aria-pressed={selectedStage === null}
        className={cn(
          'min-h-11 rounded-full px-3 py-1.5 text-sm font-medium transition-all',
          selectedStage === null
            ? 'bg-foreground text-background'
            : 'bg-muted text-muted-foreground hover:bg-surface-hover'
        )}
      >
        All ({leads.length})
      </button>
      {activeStages.map((stage) => {
        const count = stageCounts.get(stage) || 0;
        const isSelected = selectedStage === stage;
        return (
          <button
            type="button"
            key={stage}
            onClick={() => onStageClick(isSelected ? null : stage)}
            aria-pressed={isSelected}
            className={cn(
              'min-h-11 rounded-full px-3 py-1.5 text-sm font-medium transition-all',
              isSelected
                ? FUNNEL_STAGE_COLORS[stage]
                : 'bg-muted text-muted-foreground hover:bg-surface-hover'
            )}
          >
            {FUNNEL_STAGE_LABELS[stage]} ({count})
          </button>
        );
      })}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Lead Funnel (S9b) — harvest -> inbox review -> outreach connection ladder
// -> drafts/sent. Distinct from CampaignFunnel above (the BANT/decision-trace
// stage filter): this section is os_campaign_funnel's lane counts.
// ─────────────────────────────────────────────────────────────────────────

interface LaneStat {
  label: string;
  value: string;
}

function FunnelLaneRow({
  icon: LaneIcon,
  label,
  stats,
  laneKey,
}: {
  icon: typeof Inbox;
  label: string;
  stats: LaneStat[];
  laneKey: string;
}): JSX.Element {
  return (
    <div
      className="flex min-h-11 flex-wrap items-center justify-between gap-2 rounded-lg border border-border px-3 py-2"
      data-campaign-funnel-lane={laneKey}
    >
      <div className="flex items-center gap-2 text-sm font-medium text-foreground">
        <LaneIcon className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
        {label}
      </div>
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
        {stats.map((stat) => (
          <span key={stat.label} className="text-sm text-muted-foreground">
            <span className="font-semibold text-foreground">{stat.value}</span> {stat.label}
          </span>
        ))}
      </div>
    </div>
  );
}

/**
 * The harvest lane (Trigify -> mktg_shadow_outputs) is deliberately absent, not merely empty.
 * Fact-closed in the migration header (20260808010000_os_campaign_funnel.sql): a real
 * campaign_key linkage DOES exist in the harvest data, but mktg_shadow_outputs carries no RLS
 * policy for `authenticated` at all — a security_invoker join would read 0 for every
 * authenticated caller forever, which is a permanent fake zero, not a currently-empty lane. No
 * lane beats a lane that can never mean anything.
 */
function CampaignFunnelLanes({
  funnel,
  isLoading,
  error,
  onRetry,
}: {
  funnel: CampaignFunnelRow | null | undefined;
  isLoading: boolean;
  error: unknown;
  onRetry: () => void;
}): JSX.Element {
  if (isLoading) {
    return (
      <Card>
        <CardContent className="space-y-3 p-4">
          <p className="text-sm font-medium text-muted-foreground">Lead Funnel</p>
          <div className="space-y-2" role="status" aria-label="Loading lead funnel" aria-live="polite">
            <span className="sr-only">Loading lead funnel</span>
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-11 rounded-lg" />
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    const safeError = toSafeUiError(error);
    return (
      <Card>
        <CardContent className="p-4">
          <p className="mb-3 text-sm font-medium text-muted-foreground">Lead Funnel</p>
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" aria-hidden="true" />
            <AlertDescription>
              {safeError.description} {safeError.recovery}
            </AlertDescription>
          </Alert>
          {safeError.retryable && (
            <Button
              variant="outline"
              size="sm"
              className="mt-3 min-h-11 md:min-h-11"
              onClick={onRetry}
            >
              Retry
            </Button>
          )}
        </CardContent>
      </Card>
    );
  }

  if (!funnel) {
    return (
      <Card>
        <CardContent className="p-4">
          <p className="mb-2 text-sm font-medium text-muted-foreground">Lead Funnel</p>
          <p className="text-sm text-muted-foreground">Funnel data is not available for this campaign yet.</p>
        </CardContent>
      </Card>
    );
  }

  const hasKey = funnel.campaign_key !== null;

  return (
    <Card>
      <CardContent className="space-y-3 p-4">
        <p className="text-sm font-medium text-muted-foreground">Lead Funnel</p>

        {hasKey ? (
          <div className="space-y-2">
            <Badge variant="outline" className="text-xs">
              Key-level: {funnel.campaign_key}
            </Badge>
            <FunnelLaneRow
              icon={Inbox}
              label="Inbox review"
              laneKey="inbox-review"
              stats={[
                { value: formatUiNumber(funnel.inbox_pending_key), label: 'pending' },
                { value: formatUiNumber(funnel.inbox_approved_key), label: 'approved' },
              ]}
            />
            <FunnelLaneRow
              icon={UserPlus}
              label="Connection sent"
              laneKey="connection-sent"
              stats={[
                { value: formatUiNumber(funnel.outreach_connection_sent_key), label: 'sent' },
                { value: formatUiNumber(funnel.outreach_declined_key), label: 'declined' },
              ]}
            />
            <FunnelLaneRow
              icon={UserCheck}
              label="Connected"
              laneKey="connected"
              stats={[
                { value: formatUiNumber(funnel.outreach_connected_key), label: 'connected' },
              ]}
            />
          </div>
        ) : (
          <div
            className="flex min-h-11 items-center gap-2 rounded-lg border border-dashed border-border px-3 py-2 text-sm text-muted-foreground"
            data-campaign-funnel-lane="marketing-lanes-empty"
          >
            <Ban className="h-4 w-4 shrink-0" aria-hidden="true" />
            Connect a campaign_key to see inbox and outreach lane data for this campaign.
          </div>
        )}

        <FunnelLaneRow
          icon={Send}
          label="Drafts sent"
          laneKey="drafts-sent"
          stats={[
            { value: formatUiNumber(funnel.pending_drafts), label: 'pending' },
            { value: formatUiNumber(funnel.sent_count), label: 'sent' },
          ]}
        />

        {/* Credits lane: hardcoded "—", not a fake number. No attribution proof yet links
            marketing spend/credits to an individual campaign (LAW 0/S9b DESIGN IT TWICE
            sub-decision) — an empty join would silently read as "0 credits used", which is
            not knowable and would be more misleading than an explicit dash. */}
        <FunnelLaneRow
          icon={Wallet}
          label="Credits"
          laneKey="credits"
          stats={[{ value: '—', label: 'used' }]}
        />
      </CardContent>
    </Card>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Lead Row
// ─────────────────────────────────────────────────────────────────────────

function LeadRow({ lead }: { lead: CampaignLead }): JSX.Element {
  const funnelStage = parseFunnelStage(lead.decision_trace);
  const momentum = parseMomentum(lead.decision_trace);
  const bant = parseBANT(lead.decision_trace);
  const recencyClass = getRecencyColor(lead.latest_updated_at);
  const leadNameId = `campaign-lead-name-${lead.heyreach_lead_id}`;
  const openDossierLabelId = `campaign-open-dossier-label-${lead.heyreach_lead_id}`;

  const MomentumIcon = momentum.trend === 'heating' ? TrendingUp : momentum.trend === 'cooling' ? TrendingDown : Minus;

  return (
    <Link
      to={`/leads/${encodeURIComponent(lead.heyreach_lead_id)}`}
      aria-labelledby={`${openDossierLabelId} ${leadNameId}`}
      className="flex min-h-11 min-w-0 flex-col gap-3 rounded-lg border border-border p-4 transition-all hover:border-border-strong hover:shadow-elevation-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 sm:flex-row sm:items-center sm:gap-4"
    >
      <span id={openDossierLabelId} className="sr-only">
        Open dossier for
      </span>
      {/* Lead info */}
      <div className="min-w-0 w-full flex-1 sm:w-auto">
        <div className="flex items-center gap-2">
          <p
            id={leadNameId}
            className="truncate font-medium text-foreground"
            data-content-origin="external"
          >
            {lead.lead_name}
          </p>
          {lead.has_pending_draft && (
            <span className="h-2 w-2 rounded-full bg-amber-500 animate-pulse shrink-0" title="Pending draft" />
          )}
        </div>
        {lead.lead_company && (
          <p
            className="truncate text-sm text-muted-foreground"
            data-content-origin="external"
          >
            {lead.lead_company}
          </p>
        )}
      </div>

      <div className="grid w-full min-w-0 grid-cols-2 items-center gap-x-3 gap-y-2 sm:flex sm:w-auto sm:shrink-0 sm:gap-4">
        {/* Funnel stage badge */}
        <div data-campaign-lead-meta="stage">
          <Badge
            variant="outline"
            className={cn('max-w-full text-xs', FUNNEL_STAGE_COLORS[funnelStage])}
          >
            {FUNNEL_STAGE_LABELS[funnelStage]}
          </Badge>
        </div>

        {/* Momentum */}
        <div
          className="flex min-w-0 items-center gap-1"
          title={`Momentum: ${Math.round(momentum.score * 100)}%`}
          aria-label={`Momentum ${Math.round(momentum.score * 100)} percent`}
          data-campaign-lead-meta="momentum"
        >
          <MomentumIcon
            className={cn('h-3.5 w-3.5 shrink-0', MOMENTUM_COLORS[momentum.trend])}
            aria-hidden="true"
          />
          <span className={cn('text-xs', MOMENTUM_COLORS[momentum.trend])}>
            {Math.round(momentum.score * 100)}%
          </span>
        </div>

        {/* BANT dots */}
        <div
          className="flex min-w-0 items-center gap-1"
          title="BANT"
          aria-label="BANT qualification"
          data-campaign-lead-meta="bant"
        >
          {(['budget', 'authority', 'need', 'timeline'] as const).map((dim) => (
            <span
              key={dim}
              className={cn('h-2 w-2 rounded-full', BANT_COLORS[bant[dim]])}
              aria-hidden="true"
            />
          ))}
        </div>

        {/* Exchanges */}
        <div
          className="flex min-w-0 items-center gap-1 text-xs text-muted-foreground"
          aria-label={`${lead.exchange_count} exchanges`}
          data-campaign-lead-meta="exchanges"
        >
          <MessageSquare className="h-3 w-3 shrink-0" aria-hidden="true" />
          {lead.exchange_count}
        </div>

        {/* Last activity */}
        <span
          className={cn('col-span-2 min-w-0 text-xs sm:col-auto', recencyClass)}
          data-campaign-lead-meta="last-activity"
        >
          {formatUiRelativeTime(lead.latest_updated_at)}
        </span>
      </div>
    </Link>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Main Page
// ─────────────────────────────────────────────────────────────────────────

export default function CampaignBattlefield(): JSX.Element {
  const { campaignId } = useParams<{ campaignId: string }>();
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const campaign = useCampaignDetail(campaignId);
  const leadsQuery = useCampaignLeads(campaignId);
  const funnelQuery = useCampaignFunnel(campaignId);
  const archiveCampaign = useArchiveCampaign();
  const isArchiveMutationPending = useIsArchiveCampaignPending();
  const isUpdateCampaignConfigPending = useIsUpdateCampaignConfigPending();
  const [isConfigDialogOpen, setIsConfigDialogOpen] = useState(false);
  const [selectedStage, setSelectedStage] = useState<FunnelStage | null>(null);
  const [search, setSearch] = useState('');

  useDocumentTitle(
    campaign.data
      ? `${campaign.data.campaign_name} — Campaign | Tacit`
      : 'Campaign | Tacit'
  );

  const filteredLeads = useMemo(() => {
    let leads = leadsQuery.data || [];
    if (selectedStage) {
      leads = leads.filter((l) => parseFunnelStage(l.decision_trace) === selectedStage);
    }
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      leads = leads.filter(
        (l) =>
          l.lead_name.toLowerCase().includes(q) ||
          (l.lead_company && l.lead_company.toLowerCase().includes(q))
      );
    }
    return leads;
  }, [leadsQuery.data, selectedStage, search]);

  const isLoading = campaign.isLoading || leadsQuery.isLoading;

  if (isLoading) {
    return (
      <Layout>
        <main
          className="mx-auto max-w-7xl space-y-6 p-6"
          role="status"
          aria-label="Loading campaign"
          aria-live="polite"
          aria-busy="true"
        >
          <h1 className="text-2xl font-bold">Campaign</h1>
          <span className="sr-only">Loading campaign</span>
          <Skeleton className="h-8 w-64" />
          <Skeleton className="h-24 rounded-lg" />
          <Skeleton className="h-12" />
          <div className="space-y-3">
            {[1, 2, 3, 4, 5].map((i) => <Skeleton key={i} className="h-16 rounded-lg" />)}
          </div>
        </main>
      </Layout>
    );
  }

  if (campaign.error || leadsQuery.error) {
    const safeError = toSafeUiError(campaign.error ?? leadsQuery.error);
    return (
      <Layout>
        <main className="mx-auto flex min-h-[50vh] max-w-xl items-center px-4 py-8">
          <div role="alert" className="w-full rounded-xl border border-destructive/40 bg-destructive/5 p-6 text-center">
            <h1 className="text-2xl font-bold">Campaign unavailable</h1>
            <p className="mt-2 text-sm text-muted-foreground">
              {safeError.description} {safeError.recovery}
            </p>
            {safeError.retryable && (
              <Button
                variant="outline"
                className="mt-4 min-h-11 md:min-h-11"
                onClick={() => {
                  void campaign.refetch();
                  void leadsQuery.refetch();
                }}
              >
                Retry
              </Button>
            )}
          </div>
        </main>
      </Layout>
    );
  }

  if (!campaign.data) {
    return (
      <Layout>
        <div className="flex h-[50vh] items-center justify-center">
          <div className="text-center">
            <h1 className="text-2xl font-bold">Campaign not found</h1>
            <p className="mt-2 text-sm text-muted-foreground">
              This campaign may have been removed or is not available to you.
            </p>
            <Button
              variant="outline"
              className="mt-4 min-h-11 md:min-h-11"
              onClick={() => navigate('/')}
            >
              <ArrowLeft className="mr-2 h-4 w-4" /> Back to Mission Control
            </Button>
          </div>
        </div>
      </Layout>
    );
  }

  const c = campaign.data;
  const isArchived = isArchivedCampaign(c);
  const ArchiveIcon = isArchived ? ArchiveRestore : Archive;

  return (
    <Layout>
      <main className="mx-auto w-full min-w-0 max-w-7xl space-y-6 overflow-x-clip px-4 py-6 sm:px-6">
        {/* Header */}
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex min-w-0 items-center gap-3">
            <Button
              variant="ghost"
              size="icon"
              className="min-h-11 min-w-11 md:min-h-11 md:min-w-11"
              onClick={() => navigate('/')}
              aria-label="Back to Mission Control"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div className="min-w-0 flex-1">
              <div className="flex min-w-0 flex-wrap items-center gap-2">
                <h1
                  className="min-w-0 break-words text-2xl font-bold"
                  data-content-origin="domain"
                >
                  {c.campaign_name}
                </h1>
                {c.goal && (
                  <Badge variant="outline" className="max-w-full text-xs capitalize">
                    {c.goal.replace('_', ' ')}
                  </Badge>
                )}
                {isArchived && <Badge variant="secondary">Archived</Badge>}
              </div>
              {c.product_line && (
                <p
                  className="text-sm text-muted-foreground"
                  data-content-origin="domain"
                >
                  {c.product_line}
                </p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {isAdmin && (
              <>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="min-h-11 md:min-h-11"
                  onClick={() => setIsConfigDialogOpen(true)}
                  disabled={isUpdateCampaignConfigPending}
                  aria-label={`Edit campaign ${c.campaign_name}`}
                >
                  <Pencil aria-hidden="true" />
                  Edit
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="min-h-11 md:min-h-11"
                  onClick={() =>
                    archiveCampaign.mutate({
                      campaignId: c.heyreach_campaign_id,
                      archived: !isArchived,
                      campaignOrganizationId: c.organization_id,
                    })
                  }
                  disabled={!archiveCampaign.canArchive || isArchiveMutationPending}
                  aria-busy={archiveCampaign.isPending}
                  aria-label={`${isArchived ? 'Unarchive' : 'Archive'} campaign ${c.campaign_name}`}
                >
                  <ArchiveIcon aria-hidden="true" />
                  {isArchived ? 'Unarchive' : 'Archive'}
                </Button>
              </>
            )}
            {c.pending_drafts > 0 && (
              <Button
                size="sm"
                className="min-h-11 md:min-h-11"
                onClick={() => navigate('/inbox?status=pending_approval')}
              >
                <Inbox className="mr-1 h-3.5 w-3.5" />
                {c.pending_drafts} Pending
              </Button>
            )}
          </div>
        </div>

        {/* HeyReach reporting notice — os_campaign_stats aggregates
            os_linkedin_drafts, which has been static since the subscription
            lapsed on 2026-06-26. An explained zero beats an unexplained
            empty funnel. */}
        <Alert>
          <Info className="h-4 w-4" aria-hidden="true" />
          <AlertDescription>
            HeyReach reporting is paused since 2026-06-26 — these figures are historical.
          </AlertDescription>
        </Alert>

        {/* Stats row */}
        <div className="grid grid-cols-[repeat(2,minmax(0,1fr))] gap-3 sm:grid-cols-5">
          <Card>
            <CardContent className="p-3 text-center">
              <Users className="mx-auto h-4 w-4 text-blue-400" />
              <p className="mt-1 text-xl font-bold">{formatUiNumber(c.total_leads)}</p>
              <p className="text-xs text-muted-foreground">Leads</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-3 text-center">
              <Send className="mx-auto h-4 w-4 text-green-400" />
              <p className="mt-1 text-xl font-bold">{formatUiNumber(c.sent_count)}</p>
              <p className="text-xs text-muted-foreground">Sent</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-3 text-center">
              <Target className="mx-auto h-4 w-4 text-purple-400" />
              <p className="mt-1 text-xl font-bold">
                {formatUiPercent((c.response_rate ?? 0) / 100)}
              </p>
              <p className="text-xs text-muted-foreground">Response Rate</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-3 text-center">
              <MessageSquare className="mx-auto h-4 w-4 text-amber-400" />
              <p className="mt-1 text-xl font-bold">
                {c.edit_rate !== null ? formatUiPercent(c.edit_rate / 100) : '—'}
              </p>
              <p className="text-xs text-muted-foreground">Edit Rate</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-3 text-center">
              <Clock className="mx-auto h-4 w-4 text-muted-foreground" />
              <p className="mt-1 text-xl font-bold">
                {c.last_activity ? formatUiRelativeTime(c.last_activity) : '—'}
              </p>
              <p className="text-xs text-muted-foreground">Last Activity</p>
            </CardContent>
          </Card>
        </div>

        {/* Lead Funnel (S9b) — harvest/inbox/outreach/drafts lanes from os_campaign_funnel */}
        <CampaignFunnelLanes
          funnel={funnelQuery.data}
          isLoading={funnelQuery.isLoading}
          error={funnelQuery.error}
          onRetry={() => void funnelQuery.refetch()}
        />

        {/* Funnel Filter */}
        <Card>
          <CardContent className="p-4">
            <p className="mb-3 text-sm font-medium text-muted-foreground">Funnel Stages</p>
            <CampaignFunnel
              leads={leadsQuery.data || []}
              selectedStage={selectedStage}
              onStageClick={setSelectedStage}
            />
          </CardContent>
        </Card>

        {/* Search + Lead List */}
        <div className="space-y-3">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <h2 className="text-lg font-semibold">
              Leads {selectedStage && `in ${FUNNEL_STAGE_LABELS[selectedStage]}`}
              <span className="ml-2 text-sm font-normal text-muted-foreground">({filteredLeads.length})</span>
            </h2>
            <div className="relative w-full sm:w-64">
              <label htmlFor="campaign-lead-search" className="sr-only">
                Search campaign leads
              </label>
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                id="campaign-lead-search"
                placeholder="Search leads..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="min-h-11 pl-9"
              />
            </div>
          </div>

          {filteredLeads.length === 0 && (
            <Card>
              <CardContent className="p-8 text-center text-muted-foreground">
                {search ? 'No leads match your search.' : 'No leads in this campaign yet.'}
              </CardContent>
            </Card>
          )}

          <div className="space-y-2">
            {filteredLeads.map((lead) => (
              <LeadRow
                key={lead.heyreach_lead_id}
                lead={lead}
              />
            ))}
          </div>
        </div>
      </main>
      {isAdmin && isConfigDialogOpen && (
        <CampaignConfigDialog
          campaignId={c.heyreach_campaign_id}
          campaignOrganizationId={c.organization_id}
          campaignDisplayName={c.campaign_name}
          onClose={() => setIsConfigDialogOpen(false)}
        />
      )}
    </Layout>
  );
}
